import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import Swal from 'sweetalert2'

import { colsEconomicModel } from './common/CostsColData'
import { withDownloadNotificationNames } from './common/downloadNotificationIntl'
import { useColumnTextFilter } from './common/tableTextFilter'
import { useTableSort } from './common/useTableSort'
import {
	buildEmptyPagedResponse,
	getCountryIdsFromTableFilter,
	mergeCountryFilters,
	useCostsCountryCatalog,
} from './common/useCostsCountryFilters'

import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload'
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus'
import { faUpload } from '@fortawesome/free-solid-svg-icons/faUpload'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { deepEqual } from 'fast-equals'
import { NewEconomicModelModalBody } from 'pages/costs/modals/NewEconomicModelModalBody'
import { DataTablePFSEvent } from 'primereact/datatable'
import { toast } from 'react-toastify'

import {
	colorConstants,
	HardFilterWrapper,
	HardFilterWrapperHandle,
	MultipleDeleter,
	Table,
	UfinetActionButtonHandle,
	UfinetBox,
	UfinetButton,
	UfinetModal,
	UfinetSectionBox,
} from 'ufinet-web-components'
import {
	AreaType,
	AuthContext,
	Authority,
	defaultPageNumber,
	defaultPageSize,
	EmptyPagingData,
	economicModelCostsService,
	getTableTranslations,
	ICostsEconomicModelResponse,
	ICostsQueryCriteria,
	mapFromPagedResponseToPagingData,
	onSelectionChangePreventDefaultsRemoval,
	PagedResponse,
	PagingData,
	useAsync,
	useModal,
} from 'ufinet-web-functions'
import { UploadExcelModalBody } from './modals/excel/UploadExcelModalBody'

type PagedEconomicModelResponse = PagedResponse<ICostsEconomicModelResponse>

const EconomicModelPage: FC = () => {
	const intl = useIntl()
	const searchButtonRef = useRef<UfinetActionButtonHandle>()
	// Guards against an in-flight, slower search overwriting the results of a newer one (e.g. clicking
	// Buscar right after typing a filter): only the response of the latest request is applied.
	const requestIdRef = useRef(0)
	const [economicModelResponse, setEconomicModelResponse] = useState<ICostsEconomicModelResponse[]>([])
	const { sortField, sortOrder, handleSort, sortData } = useTableSort<ICostsEconomicModelResponse>()
	const [pagingData, setPagingData] = useState<PagingData>(EmptyPagingData)
	const [pagingState, setPagingState] = useState({ pageNumber: defaultPageNumber, pageSize: defaultPageSize })
	const [modal, showModal, hideModal] = useModal()
	const [newModal, showNewModal, hideNewModal] = useModal()
	const [selectedValues, setSelectedValues] = useState<ICostsEconomicModelResponse[]>()
	const [tableCountryFilterIds, setTableCountryFilterIds] = useState<string[]>([])

	const authData = useContext(AuthContext)
	const { countryFilterOptions } = useCostsCountryCatalog(authData)
	const roles = authData.userData?.authorities || []
	const permissions = Authority.getCostsPermissions(roles)

	const [loadingRecords, setLoadingRecords] = useState<boolean>(false)

	const _economicModelCostsService = useMemo(() => economicModelCostsService(authData, intl), [authData, intl])
	// Free-text Cliente/Área filters: see tableTextFilter.tsx for why (broken built-in select match mode).
	const clientNameFilter = useColumnTextFilter('COST.CLIENT')
	const areaNameFilter = useColumnTextFilter('COST.AREA')
	const updatedByFilter = useColumnTextFilter('COST.UPDATEDBY')
	const tableCols = useMemo(
		() =>
			colsEconomicModel.map((column) => {
				if (column.field === 'countryName') return { ...column, filterOptions: countryFilterOptions }
				if (column.field === 'clientName') return clientNameFilter.applyToColumn(column)
				if (column.field === 'networkAreaName') return areaNameFilter.applyToColumn(column)
				if (column.field === 'lastModifiedBy') return updatedByFilter.applyToColumn(column)
				return column
			}),
		[countryFilterOptions, clientNameFilter.applyToColumn, areaNameFilter.applyToColumn, updatedByFilter.applyToColumn]
	)

	const upperFilterRef = useRef<HardFilterWrapperHandle>(null)

	// Reset to page 1 whenever the free-text Cliente/Área/Actualizado por search term changes, so results
	// from the new search aren't hidden behind a page number that no longer makes sense.
	useEffect(() => {
		setPagingState((prev) => ({ ...prev, pageNumber: defaultPageNumber }))
	}, [clientNameFilter.value, areaNameFilter.value, updatedByFilter.value])

	const getEconomicModelCosts = useCallback(async (): Promise<PagedEconomicModelResponse | null> => {
		const requestId = ++requestIdRef.current
		setLoadingRecords(true)
		searchButtonRef.current?.changeActionStatus(true)
		const hardFilterCountryIds = upperFilterRef.current?.getCountries().map((e) => e.value) || []
		const { countries, hasImpossibleCountryFilter } = mergeCountryFilters(hardFilterCountryIds, tableCountryFilterIds)
		if (hasImpossibleCountryFilter) {
			return buildEmptyPagedResponse<ICostsEconomicModelResponse>(pagingState.pageNumber, pagingState.pageSize)
		}

		// Cliente/Área are searched server-side (LIKE '%term%', see aqs-backend's
		// clientNameSearch/networkAreaNameSearch) so results cover the whole dataset, not just this page.
		const criteria: ICostsQueryCriteria & {
			pageNumber: number
			pageSize: number
			clientNameSearch?: string
			networkAreaNameSearch?: string
			updatedBy?: string
		} = {
			countries,
			networkAreas: upperFilterRef.current?.getAreas().map((e) => e.value) || [],
			pageNumber: pagingState.pageNumber,
			pageSize: pagingState.pageSize,
			clientNameSearch: clientNameFilter.value,
			networkAreaNameSearch: areaNameFilter.value,
			updatedBy: updatedByFilter.value,
		}
		const result = (await _economicModelCostsService.findByCriteria(criteria)) as unknown as PagedEconomicModelResponse
		return requestId === requestIdRef.current ? result : null
	}, [
		_economicModelCostsService,
		pagingState.pageNumber,
		pagingState.pageSize,
		tableCountryFilterIds,
		clientNameFilter.value,
		areaNameFilter.value,
		updatedByFilter.value,
	])

	const onEconomicModelCostsFetched = (response: PagedEconomicModelResponse | null): void => {
		if (!response) return // superseded by a newer search; ignore this stale response
		const items = Array.isArray(response) ? (response as unknown as ICostsEconomicModelResponse[]) : response.data
		const parsedCosts = items.map((cost) => ({
			...cost,
			networkAreaName: getTableTranslations(cost.networkAreaName, intl),
			clientName: getTableTranslations(cost.clientName, intl),
		}))

		if (!Array.isArray(response)) {
			setPagingData(mapFromPagedResponseToPagingData(response))
		}
		setEconomicModelResponse(parsedCosts)
	}

	const onEconomicModelCostsFailedToFetch = (): void => {
		toast.error(intl.formatMessage({ id: 'COSTS.FETCH.ERROR' }))
	}

	const runFinally = () => {
		setLoadingRecords(false)
		searchButtonRef.current?.changeActionStatus(false)
	}

	// Collapse every filter/paging input into one string signature and drive the fetch off a single
	// incrementing token, so one user action = exactly one /find call. useAsync's internal effect compares
	// its deps per-element; passing the raw filter arrays made it re-fire on every new array reference. A
	// primitive token gated by the signature ignores that reference churn.
	const [searchToken, setSearchToken] = useState(0)
	const criteriaSignature = useMemo(
		() =>
			JSON.stringify([
				pagingState.pageNumber,
				pagingState.pageSize,
				tableCountryFilterIds,
				clientNameFilter.value,
				areaNameFilter.value,
				updatedByFilter.value,
			]),
		[
			pagingState.pageNumber,
			pagingState.pageSize,
			tableCountryFilterIds,
			clientNameFilter.value,
			areaNameFilter.value,
			updatedByFilter.value,
		]
	)
	useEffect(() => {
		setSearchToken((token) => token + 1)
	}, [criteriaSignature])

	const fetchAndStore = useAsync(
		{
			asyncFn: getEconomicModelCosts,
			onSuccess: onEconomicModelCostsFetched,
			onFailure: onEconomicModelCostsFailedToFetch,
			runFinally: runFinally,
			// skipMount: the initial load is triggered once by the signature effect below (token 0 -> 1),
			// so we avoid a duplicate fetch on first render.
			options: { skipMount: true },
		},
		[searchToken]
	)

	const handlePage = (event: DataTablePFSEvent) => {
		setPagingState({ pageNumber: event.page ?? defaultPageNumber, pageSize: event.rows ?? defaultPageSize })
	}
	const handleFilter = (event: DataTablePFSEvent) => {
		const countryIds = getCountryIdsFromTableFilter(event)
		setPagingState((prev) => ({ ...prev, pageNumber: defaultPageNumber }))
		setTableCountryFilterIds(countryIds)
	}

	/// //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
	/// /////////////////////////////////////// HEADER TABLE AND ACTION BUTTONS //////////////////////////////////////////
	/// //////////////////////////////////////////////////////////////////////////////////////////////////////////////////

	const getActionButtons = () => {
		return (
			<>
				<OverlayTrigger
					placement="bottom"
					delay={{ show: 250, hide: 400 }}
					overlay={<Tooltip className="tooltip-dark">{intl.formatMessage({ id: 'DOWNLOAD_EXCEL' })}</Tooltip>}
				>
					<div className="d-inline">
						<FontAwesomeIcon
							className="fa-icon-primary"
							size="1x"
							icon={faDownload}
							onClick={() => {
								const hardFilterCountryIds = upperFilterRef.current?.getCountries().map((e) => e.value) || []
								const { countries, hasImpossibleCountryFilter } = mergeCountryFilters(
									hardFilterCountryIds,
									tableCountryFilterIds
								)
								if (hasImpossibleCountryFilter) return
								const areaOptions = upperFilterRef.current?.getAreas() || []
								const idToName = new Map([
									...(countryFilterOptions?.map((o): [string, string] => [o.value, o.label]) || []),
									...areaOptions.map((o): [string, string] => [o.value, o.label]),
								])
								economicModelCostsService(authData, withDownloadNotificationNames(intl, idToName)).downloadXLS({
									countries,
									networkAreas: areaOptions.map((e) => e.value),
								})
							}}
						/>
					</div>
				</OverlayTrigger>
			</>
		)
	}

	const getHeaderButtons = () => (
		<>
			{permissions.canDelete && (
				<MultipleDeleter
					setSelectedValues={setSelectedValues}
					selectedValues={selectedValues}
					deleteEndpoint={_economicModelCostsService.deleteByIdsList}
					search={fetchAndStore}
				/>
			)}
			{permissions.canWrite && (
				<>
					<UfinetButton
						className="me-3"
						icon={faPlus}
						onClick={() => showNewModal()}
						content={intl.formatMessage({ id: 'NEW.REGISTER' })}
					/>
					<UfinetButton icon={faUpload} onClick={() => showModal()} content={intl.formatMessage({ id: 'BULK_LOAD' })} />
				</>
			)}
		</>
	)

	const onRowEditComplete = (data: ICostsEconomicModelResponse) => {
		if (
			deepEqual(
				data,
				economicModelResponse.find((it) => it.id === data.id)
			)
		)
			return

		Swal.fire({
			icon: 'warning',
			title: intl.formatMessage(
				{ id: 'COUNTRY.REGISTRY.UPDATE.CLIENT' },
				{ countryName: data.countryName, clientName: data.clientName }
			),
			html: intl.formatMessage({ id: 'COUNTRY.REGISTRY.UPDATE.CONFIRM' }, { networkAreaName: data.networkAreaName }),
			showCancelButton: true,
			showConfirmButton: true,
			reverseButtons: true,
			confirmButtonColor: colorConstants.primaryColor,
			cancelButtonColor: colorConstants.dangerColor,
			preConfirm: () => {
				const { id, countryName, networkAreaName, investmentMargin, recurringMargin, incrementMrc, incrementNrc } = data
				_economicModelCostsService
					.update(id, {
						countryName,
						networkAreaName,
						investmentMargin,
						recurringMargin,
						incrementMrc,
						incrementNrc,
					})
					.then(fetchAndStore)
					.catch(console.error)
			},
		})
	}

	const onSelectionChange = (values: ICostsEconomicModelResponse[]) => {
		onSelectionChangePreventDefaultsRemoval(
			values,
			selectedValues,
			economicModelResponse,
			setSelectedValues,
			intl.formatMessage({ id: 'BY_DEFAULT.DELETE.WARNING' })
		)
	}

	return (
		<UfinetBox>
			<HardFilterWrapper
				ref={upperFilterRef}
				actionButtonRef={searchButtonRef}
				search={fetchAndStore}
				networkAreaType={AreaType.ECONOMIC_MODEL_AREA}
				actionButtons={getActionButtons()}
			/>
			<UfinetSectionBox>
				<Table
					className="fs-6"
					cols={tableCols}
					content={sortData(economicModelResponse)}
					sortField={sortField}
					sortOrder={sortOrder}
					headerButtons={getHeaderButtons()}
					onFilterClear={() => {
						clientNameFilter.reset()
						areaNameFilter.reset()
						updatedByFilter.reset()
					}}
					selectedValues={selectedValues}
					onSelectionChange={permissions.canDelete ? onSelectionChange : undefined}
					onRowEditComplete={permissions.canUpdate ? onRowEditComplete : undefined}
					emptyMessage={loadingRecords ? intl.formatMessage({ id: 'LOADING_DOTS' }) : undefined}
					lazySettings={{
						...pagingData,
						loading: loadingRecords,
						onPage: handlePage,
						onSort: handleSort,
						onFilter: handleFilter,
					}}
				/>
			</UfinetSectionBox>
			{/* Modals */}
			<UfinetModal
				size="xl"
				show={modal}
				handleClose={hideModal}
				title={intl.formatMessage({ id: 'MODAL.UPLOAD_EXCEL.TITLE' })}
			>
				<UploadExcelModalBody
					xlsEmptyDownloadCallback={_economicModelCostsService.downloadEmptyListXLS}
					xlsUploadCallback={_economicModelCostsService.uploadXLS}
					onXlsUploaded={fetchAndStore}
					hideModal={hideModal}
				/>
			</UfinetModal>
			<UfinetModal
				size="lg"
				show={newModal}
				handleClose={hideNewModal}
				title={intl.formatMessage({ id: 'MODAL.NEW.REGISTER.TITLE' })}
			>
				<NewEconomicModelModalBody findCosts={fetchAndStore} hideModal={hideNewModal} />
			</UfinetModal>
		</UfinetBox>
	)
}

export { EconomicModelPage }
